import React from "react";
import {Link, useLocation, Route, Outlet} from "react-router-dom";
import {Menu} from "antd";

import PageNotFound from "../components/PageNotFound";
import {RoutesConfig, SidebarLinks, TestRoutes, TestSidebarLinks} from "./routesConfig";
import {isModuleAvailable} from "../lib/site";

const mobileBreakpoint = 768;

export interface RouteConfig {
    path: string;
    component: () => React.JSX.Element;
    staffComponent?: () => React.JSX.Element;
    hasStaff?: boolean;
    membershipRequired?: boolean;
    staffOnly?: boolean;
    module?: string;
    businessRoute?: boolean;
}

/**
 * Build routes based on the routes config file.
 */
interface BuildRoutesProps {
    isStaff: boolean;
    hasMembership: boolean;
    isBusiness: boolean;
}

const buildRoutes = ({isStaff, hasMembership, isBusiness}: BuildRoutesProps) => {
    let routes: (RouteConfig)[] = RoutesConfig;
    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        routes = [...TestRoutes, ...routes];
    }

    return routes.map((route, index) => (
        buildSingleRoute({index, routeInfo: route, isStaff, hasMembership, isBusiness})
    ));
};

/**
 * Constructs a single route component.
 */
interface BuildSingleRouteProps {
    index: number;
    routeInfo: RouteConfig;
    isStaff: boolean;
    hasMembership: boolean;
    isBusiness: boolean;
}

const buildSingleRoute = ({index, routeInfo, isStaff, hasMembership, isBusiness}: BuildSingleRouteProps) => {
    let Component = PageNotFound;

    // If the user has no membership and the component requires one, disable the route.
    const hideComponentNoMembership = !hasMembership && routeInfo.membershipRequired;

    if (routeInfo.module === undefined || isModuleAvailable(routeInfo.module)) {

        // If not staff check if the user has an active membership and if the component requires one.
        if (isStaff) {
            // Neither staffOnly nor hasStaff, therefore public, return component
            if (routeInfo.staffOnly === undefined && routeInfo.hasStaff === undefined) {
                Component = routeInfo.component;
            }
        } else {
            // Neither staffOnly nor hasStaff, therefore public, return component
            if (routeInfo.staffOnly === undefined && routeInfo.hasStaff === undefined && !hideComponentNoMembership) {
                Component = routeInfo.component;
            }
        }

        // Has staffOnly, only return if they're staff.
        if (routeInfo.staffOnly === true && isStaff) {
            if (routeInfo.businessRoute !== undefined) {
                Component = isBusiness ? routeInfo.component : Component;
            } else {
                Component = routeInfo.component;
            }
        }

        // Has hasStaff, meaning there's a public member facing component as well as a staff one.
        if (routeInfo.hasStaff && routeInfo.hasStaff) {
            if (isStaff) {
                // Check if it's a business route and a business account.
                if (routeInfo.businessRoute !== undefined) {
                    Component = isBusiness && routeInfo.staffComponent !== undefined ? routeInfo.staffComponent : Component;
                } else {
                    Component = routeInfo.staffComponent !== undefined ? routeInfo.staffComponent : PageNotFound;
                }
                // Check if it's a business route and a business account.
            } else if (routeInfo.businessRoute !== undefined) {
                Component = isBusiness ? routeInfo.component : Component;

            } else if (!hideComponentNoMembership) {
                Component = routeInfo.component;
            }
        }
    }

    return (
        <Route key={index} element={<Outlet />}>
            <Route
                key={index}
                {...routeInfo}
                element={<Component/>}
             />
        </Route>
    )
};

/**
 * Builds sidebar links based on the routes config file. The amount of additional logic in here is because of this:
 * When a user navigates with the sidebar, the submenus open/collapse and highlight depending on what has been clicked.
 * The URL and sidebar are now responsively linked to one another.
 */
interface BuildSidebarLinksProps {
    menuReduced: boolean; // On desktop and tablet the menu can reduce to be one line of icons.
    menuCollapsed: boolean; // On mobile this is when the menu is completely collapsed, when open takes up entire screen.
    handleMobileLinkClicked: () => void; // Handles collapsing the sidebar so the user can use a page.
    isStaff: boolean;
    isBusiness: boolean;
    hasMembership: boolean;
}

const BuildSidebarLinks = ({
    menuReduced,
    menuCollapsed,
    handleMobileLinkClicked,
    isStaff,
    isBusiness,
    hasMembership,
}: BuildSidebarLinksProps) => {
    const location = useLocation();
    let sidebarLinks = SidebarLinks;

    // Passed into the AntD <Menu /> component for which submenu are open and selected.
    const [updateKeys, setUpdateKeys] = React.useState(false);
    const [openKeys, setOpenKeys] = React.useState<string[]>([])
    const [selectedKeys, setSelectedKeys] = React.useState<string[]>([]);

    // We need to use refs because handleMobileLinkClicked collapses the sidebar
    // as on mobile the user needs it closed to use a page. This re-renders the component
    // hence resetting the openKeys and selectedKeys to their default values.
    const mobileOpenRef = React.useRef<string[]>([]);
    const mobileSelectedRef = React.useRef<string[]>([]);

    // History event listener to change updateKeys to trigger the use effect below this one.
    React.useEffect(() => {
        // return location.listen((location) => {
            setUpdateKeys(prevState => !prevState);
        // })
    }, [location]);

    // On mount, or when the url changes parse the url, and update the keys.
    React.useEffect(() => {
        let menuKey;
        let subMenuKey;
        let arrOfSlash = [];
        let path = location.pathname;

        // The path can be of varying length, loop through and create an array of the '/' indices.
        for (let i = 0; i < path.length; i++){
            if (path.charAt(i) === '/') arrOfSlash.push(i);
        }

        // A path can have 1, 2, or more '/' we only care about indices 0, 1, and 2.
        // Depending on how many retrieve the menuKey and subMenuKey.
        // EG: /account/account_info => /{menuKey}/{subMenuKey}
        switch (arrOfSlash.length){
            case 1:
                menuKey = path.slice(1, path.length);
                break;
            case 2:
                menuKey = path.slice(1, arrOfSlash[1]);
                subMenuKey = path.slice(arrOfSlash[1] + 1, path.length);
                break;
            default:
                menuKey = path.slice(1, arrOfSlash[1]);
                subMenuKey = path.slice(arrOfSlash[1] + 1, arrOfSlash[2]);
                break;
        }

        // On mobile when a user clicks back, we don't want to update state as the menu should remain closed.
        // Otherwise, the menu opens and creates a graphical bug and if the menu is reduced don't update the key.
        if (window.innerWidth >= mobileBreakpoint && !menuReduced){
            setOpenKeys([menuKey])
            setSelectedKeys(subMenuKey ? [subMenuKey] : []);
        }

        mobileOpenRef.current = [menuKey];
        mobileSelectedRef.current = subMenuKey ? [subMenuKey] : [];

    }, [menuReduced, updateKeys, location])

    // This is required for mobile as the sidebar is collapsed upon clicking a link.
    // A sub menu should only be open if the entire sidebar is.
    React.useEffect(() => {
        if (mobileOpenRef.current.length !== 0) {
            setOpenKeys(mobileOpenRef.current);
        }

        if (mobileSelectedRef.current.length !== 0) {
           setSelectedKeys(mobileSelectedRef.current) ;
        }

    }, [menuReduced, menuCollapsed])

    // Generic handler for when more than one submenu is open.
    const handleOpenChange = (e: string[]) => {
        setOpenKeys(e);
    }

    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        sidebarLinks = [...sidebarLinks, ...TestSidebarLinks];
    }

    sidebarLinks = sidebarLinks.map((menuItem, index) => {
        return buildSidebarLink({index, menuItem, isStaff, hasMembership, isBusiness});
    }).filter(item => item !== null);

    return (
        <Menu
            theme="dark"
            mode="inline"
            items={sidebarLinks}
            selectedKeys={selectedKeys}
            openKeys={openKeys}
            onClick={handleMobileLinkClicked}
            onOpenChange={handleOpenChange}
        />
    );
};

/**
 * Constructs a single sidebar menu item
 */
interface BuildSidebarLinkProps {
    index: number;
    menuItem: {
        label: string;
        key: string;
        icon: React.ReactElement,
        membershipRequired?: boolean;
        staffOnly?: boolean;
        module?: string;
        businessComponent?: boolean;
        path?: string;
        children: {
            label: string;
            key: string;
            path: string;
            module?: string;
            staffOnly?: boolean;
            membershipRequired?: boolean;
        }[],
    },
    isStaff: boolean;
    hasMembership: boolean;
    isBusiness: boolean;
}

const buildSidebarLink = ({menuItem, isStaff, hasMembership, isBusiness}: BuildSidebarLinkProps) => {
    const hideComponentNoMembership = !hasMembership && menuItem.membershipRequired;

    // Return early if this MenuItem is restricted to staff
    if (menuItem.staffOnly && !isStaff) {
        return null;
    }
    if (menuItem.module !== undefined && !isModuleAvailable(menuItem.module)) {
        return null;
    }
    // Return early if user is not staff, and there is no active membership.
    if (!isStaff && hideComponentNoMembership === true) {
        return null;
    }
    // If it is a business subMenu and the active account is not a business: hide the submenu.
    if (menuItem.businessComponent === true && !isBusiness){
        return null;
    }

    let builtChildren: (null | { label: React.JSX.Element; key: string })[] = [];

    if (menuItem.children !== undefined) {
        builtChildren = menuItem.children.map((item) => {
            // Check to see if any of the submenu items should not be included.
            // Otherwise, build a link element.
            if (
                (item.staffOnly && !isStaff) ||
                (item.module !== undefined && !isModuleAvailable(item.module)) ||
                (menuItem.businessComponent === true && !isBusiness) ||
                (!isStaff && (!hasMembership && item.membershipRequired))
            ) {
                return null;
            } else {
                return (
                    {
                        label: (
                            <Link to={item.path}>
                                <span className="nav-text">{item.label}</span>
                            </Link>
                        ),
                        key: item.key,
                    }
                )
            }
        })
    }

    // Filter the submenu items out.
    builtChildren = builtChildren.filter(item => item !== null);

    // If it is a submenu with children then pass in the builtChildren variable.
    // Otherwise, return a raw <Link> element.
    if (builtChildren.length !== 0) {
        return (
            {
                label: menuItem.label,
                key: menuItem.key,
                icon: menuItem.icon,
                children: builtChildren,
            }
        )
    } else {
        return (
            {
                label: (
                    <Link to={menuItem.path ?? ''}>
                        <span className="nav-text">{menuItem.label}</span>
                    </Link>
                ),
                icon: menuItem.icon,
                key: menuItem.key,
            }
        )
    }
};

export {
    buildRoutes,
    BuildSidebarLinks,
};
